
import { supabase } from '@/integrations/supabase/client';
import { validateCourseCompletionForCertificate } from '@/hooks/auth/progressService';
import CertificateService from '@/components/course/certificate/CertificateService';
import { toast } from '@/components/ui/sonner';

interface CourseCompletionData {
  userId: string;
  courseId: string;
  courseType: string;
  courseTitle: string;
  userName: string;
  quizAverage: number;
}

export class CourseCompletionService {
  // Monitor course completion and auto-generate certificates
  // Phase 2: STRICT validation with module completion checks
  static async checkAndGenerateCertificate(
    userId: string,
    courseId: string,
    courseType: string,
    courseTitle: string,
    userName: string
  ): Promise<{ success: boolean; certificate?: any; message?: string }> {
    try {
      console.log(`[CourseCompletion] Starting STRICT certificate generation for ${userName} in ${courseTitle}`);
      
      // Phase 2: STRICT validation with module completion checks
      const validation = await validateCourseCompletionForCertificate(userId, courseId);
      
      console.log(`[CourseCompletion] Validation:`, {
        canIssue: validation.canIssue,
        completedModules: validation.completedModules,
        totalModules: validation.totalModules,
        reason: validation.reason
      });
      
      if (!validation.canIssue) {
        console.log(`[CourseCompletion] ❌ Certificate not eligible: ${validation.reason}`);
        return { 
          success: false, 
          message: validation.reason || 'Course completion requirements not met' 
        };
      }

      // Emergency circuit breaker - double-check modules
      if (validation.completedModules !== validation.totalModules) {
        console.error(`[CourseCompletion] ⚠️ Circuit breaker: Modules incomplete`);
        return {
          success: false,
          message: `Complete all ${validation.totalModules} modules before earning certificate`
        };
      }
      
      console.log(`[CourseCompletion] ✅ Validation passed`);

      // Check if certificate already exists
      let existingCertificate = await CertificateService.getCertificate(userId, courseId);
      
      if (existingCertificate) {
        return {
          success: true,
          certificate: existingCertificate,
          message: 'Certificate already exists'
        };
      }

      // Generate new certificate
      const certificate = await CertificateService.storeCertificate(
        userId,
        courseId,
        courseTitle,
        userName,
        validation.quizAverage || 0
      );

      if (certificate) {
        // Send notification
        await this.sendCertificateNotification(userId, certificate);
        
        // Log the achievement
        await this.logCertificateGeneration(userId, courseId, certificate.certificateNumber);
        
        console.log(`Certificate generated successfully: ${certificate.certificateNumber}`);
        
        return {
          success: true,
          certificate,
          message: 'Certificate generated successfully!'
        };
      }

      return {
        success: false,
        message: 'Failed to generate certificate'
      };
    } catch (error) {
      console.error('Error in certificate generation:', error);
      return {
        success: false,
        message: 'Certificate generation failed due to system error'
      };
    }
  }

  // Send notification when certificate is generated
  static async sendCertificateNotification(userId: string, certificate: any): Promise<void> {
    try {
      await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title: '🎉 Certificate Earned!',
          message: `Congratulations! Your NeoCertify certificate for ${certificate.courseTitle} has been issued. Certificate Number: ${certificate.certificateNumber}`,
          type: 'certificate_issued',
          data: {
            certificate_id: certificate.id,
            certificate_number: certificate.certificateNumber,
            course_id: certificate.courseId,
            course_title: certificate.courseTitle
          }
        });
    } catch (error) {
      console.error('Error sending certificate notification:', error);
    }
  }

  // Log certificate generation for audit trail
  static async logCertificateGeneration(userId: string, courseId: string, certificateNumber: string): Promise<void> {
    try {
      await supabase
        .from('module_activity_logs')
        .insert({
          user_id: userId,
          module_id: `${courseId}-completion`,
          course_id: courseId,
          activity_type: 'certificate_generated',
          metadata: {
            certificate_number: certificateNumber,
            generated_at: new Date().toISOString(),
            auto_generated: true
          }
        });
    } catch (error) {
      console.error('Error logging certificate generation:', error);
    }
  }

  // Check all completed courses for users who haven't received certificates yet
  static async processPendingCertificates(): Promise<{ processed: number; generated: number }> {
    try {
      console.log('Processing pending certificates...');
      
      // Get all course assignments with 100% completion but no certificate
      const { data: completedAssignments } = await supabase
        .from('course_assignments')
        .select(`
          user_id,
          course_id,
          final_grade
        `)
        .eq('status', 'completed')
        .gte('final_grade', 100);

      let processed = 0;
      let generated = 0;

      if (completedAssignments) {
        for (const assignment of completedAssignments) {
          processed++;
          
          // Check if certificate already exists
          const existingCert = await CertificateService.getCertificate(
            assignment.user_id,
            assignment.course_id
          );

          if (!existingCert) {
            // Get user profile
            const { data: profile } = await supabase
              .from('profiles')
              .select('name')
              .eq('id', assignment.user_id)
              .maybeSingle();

            // Attempt to generate certificate
            const result = await this.checkAndGenerateCertificate(
              assignment.user_id,
              assignment.course_id,
              'unknown', // Will be enhanced with proper course type detection
              assignment.course_id,
              profile?.name || 'Student'
            );

            if (result.success) {
              generated++;
            }
          }
        }
      }

      console.log(`Processed ${processed} completed courses, generated ${generated} new certificates`);
      return { processed, generated };
    } catch (error) {
      console.error('Error processing pending certificates:', error);
      return { processed: 0, generated: 0 };
    }
  }
}
